/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	IIntegrationRefFunctionItem,
	IIntegrationRefInstanceItem,
	IIntegrationRefResolver,
	IIntegrationRefTypeItem,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { createEventEmitter, emitEvent, offEvent, onEvent } from "@adapptio/lib-shared";
import { ResourceService } from "../../resources/ResourceService";
import { IEditorResource_Integration } from "../../resources/ResourceTypes";
import { IntegrationService } from "../IntegrationService";

/**
 * Integration Ref Resolver
 */
export class IntegrationRefResolver implements IIntegrationRefResolver {
	/** Invalidate event */
	public onInvalidate = createEventEmitter<void>();

	private invalidateHandler: (resourceType: RESOURCE_TYPES) => void;

	/**
	 * Constructor
	 */
	public constructor(private resourceSvc: ResourceService, private integrationSvc: IntegrationService) {
		this.invalidateHandler = (resourceType) => {
			if (resourceType === RESOURCE_TYPES.INTEGRATION) {
				emitEvent(this.onInvalidate);
			}
		};

		onEvent(this.resourceSvc.onResourceTypeChange, this.invalidateHandler);
	}

	/**
	 * Returns a list of available integration types
	 */
	public getTypeList(): IIntegrationRefTypeItem[] {
		return this.integrationSvc.getIntegrationTypes();
	}

	/**
	 * Returns integration type information by given type
	 */
	public getTypeInfo(integrationType: string): IIntegrationRefTypeItem {
		return this.integrationSvc.getIntegrationType(integrationType);
	}

	/**
	 * Returns if a given integration type exists
	 */
	public typeExists(integrationType: string): boolean {
		return this.integrationSvc.integrationTypeExists(integrationType);
	}

	/**
	 * Returns integration instance list for a given type
	 */
	public getInstanceListByType(integrationType: string): IIntegrationRefInstanceItem[] {
		return this.resourceSvc
			.getResourceListByType(RESOURCE_TYPES.INTEGRATION)
			.filter((resource: IEditorResource_Integration) => resource.integrationType === integrationType)
			.map((resource) => ({
				integrationId: resource.id,
				label: resource.descriptor.name,
				description: null
			}));
	}

	/**
	 * Returns integration instance info by given type and id
	 */
	public getInstanceInfo(integrationType: string, integrationId: string): IIntegrationRefInstanceItem {
		const resource = this.resourceSvc.getResourceById(integrationId) as IEditorResource_Integration;

		if (resource && resource.integrationType === integrationType) {
			return {
				integrationId: resource.id,
				label: resource.descriptor.name,
				description: null
			};
		} else {
			return null;
		}
	}

	/**
	 * Returns if instance with given type and id exists
	 */
	public instanceExists(integrationType: string, integrationId: string): boolean {
		const resource = this.resourceSvc.getResourceById(integrationId) as IEditorResource_Integration;
		return resource && resource.integrationType === integrationType;
	}

	/**
	 * Returns integration function list for a given type
	 */
	public getFunctionList(integrationType: string): IIntegrationRefFunctionItem[] {
		return this.integrationSvc.getIntegrationFunctions(integrationType);
	}

	/**
	 * Returns integration function information by given type and function name
	 */
	public getFunctionInfo(integrationType: string, functionName: string): IIntegrationRefFunctionItem {
		return this.integrationSvc.getSingleIntegrationFunction(integrationType, functionName);
	}

	/**
	 * Returns a params schema by action ID
	 */
	public getFunctionParamsSchema(integrationType: string, functionName: string): TSchemaConstObjectProps {
		return (
			this.integrationSvc.getSingleIntegrationFunction(integrationType, functionName)?.paramsSchema.opts
				.props || null
		);
	}

	/**
	 * Disposes resolver
	 */
	public dispose(): void {
		offEvent(this.resourceSvc.onResourceTypeChange, this.invalidateHandler);
	}
}
